import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../../ui/gzRadio';
import { translateFn } from '../../../../../utils/translator.helper';
import { TemplateState } from '../../../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../ui/gzDropdown';
import { defaultByHourTime, defaultStartTime, RecurrenceType, RECURRENCE_OPTIONS, WeekDays } from '../types';
import { SchedulerMode } from '../types/SchedulerMode';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { GzInputNumberWebComponent } from '../../../../../ui/gzInput';
import { GzTimePickerWebComponent } from '../../../../../ui/gzTimePicker';
import { SelectedTime } from '../../../../../ui/gzTimePicker/types';
import { GzMultiSelectGroupWebComponent } from '../../../../../ui/gzMultiSelect';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';

export class ReconfigureAgentSchedulerFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public dom: {
    $wrapper: HTMLElement;
    $scheduledSection: HTMLElement;

    $reconfigureSchedulerFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $recurrenceFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $recurrenceByHourFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
    $recurrenceDailyStartTimeFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, SelectedTime>;
    $recurrenceWeeklyFormField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent, Array<string | number>>;

    $reconfigureSchedulerGroup: GzRadioGroupWebComponent;
    $reconfigureSchedulerNow: GzRadioWebComponent;
    $reconfigureSchedulerScheduled: GzRadioWebComponent;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('#reconfigure-agent-scheduler-form-wrapper'),
      $scheduledSection: this.shadow.querySelector('#scheduled-section'),

      $recurrenceFormField: this.shadow.querySelector('#recurrence-form-field'),
      $reconfigureSchedulerFormField: this.shadow.querySelector('#reconfigure-scheduler-options'),
      $recurrenceByHourFormField: this.shadow.querySelector('#recurrence-evey-day-form-field'),
      $recurrenceDailyStartTimeFormField: this.shadow.querySelector('#recurrence-start-time-form-field'),
      $recurrenceWeeklyFormField: this.shadow.querySelector('#recurrence-weekly-select-form-field'),

      $reconfigureSchedulerGroup: this.shadow.querySelector('#reconfigure-scheduler-options-group'),
      $reconfigureSchedulerNow: this.shadow.querySelector('#reconfigure-scheduler-now'),
      $reconfigureSchedulerScheduled: this.shadow.querySelector('#reconfigure-scheduler-scheduled'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getSchedulerStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$recurrenceFormField.label = translateFn('Recurrence');
    this.dom.$recurrenceByHourFormField.label = translateFn('Every');
    this.dom.$recurrenceDailyStartTimeFormField.label = translateFn('Start Time');
    this.dom.$recurrenceWeeklyFormField.label = translateFn('On');

    this.dom.$reconfigureSchedulerNow.label = translateFn('Now');
    this.dom.$reconfigureSchedulerScheduled.label = translateFn('Scheduled');
  }

  buildSuccessView(data: any): void {
    this.dom.$reconfigureSchedulerNow.value = SchedulerMode.Now;
    this.dom.$reconfigureSchedulerScheduled.value = SchedulerMode.Scheduled;

    this.setReconfigureSchedulerDropdownOptions();
    this.setDefaultWeekDays();

    this.createFormGroup();
    this.setDefaultValues();
    this.addValidators();
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private addValidators(): void {
    this.dom.$recurrenceWeeklyFormField.Validators = [
      RequiredValidator(translateFn('You must select one day of the week')),
    ];
  }

  private setDefaultValues(): void {
    this.formGroup.value = {
      settings: {
        specifics: {
          scheduler: {
            scheduledDeploy: SchedulerMode.Now,
            scheduleType: RECURRENCE_OPTIONS[RecurrenceType.ByHour].value,
            everyHour: defaultByHourTime,
            startTime: defaultStartTime,
            dayOfWeek: [WeekDays.Monday],
          },
        },
      },
    };
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      settings: {
        specifics: {
          scheduler: {
            scheduledDeploy: this.dom.$reconfigureSchedulerFormField,
            scheduleType: this.dom.$recurrenceFormField,
            everyHour: this.dom.$recurrenceByHourFormField,
            startTime: this.dom.$recurrenceDailyStartTimeFormField,
            dayOfWeek: this.dom.$recurrenceWeeklyFormField,
          },
        },
      },
    });
  }

  private setStartTimeAndByHourDefaultValue(): void {
    this.dom.$recurrenceDailyStartTimeFormField.value = defaultStartTime;
    this.dom.$recurrenceByHourFormField.value = defaultByHourTime;
  }

  private setDefaultWeekDays(): void {
    const daysArray: string[] = Object.keys(WeekDays).filter(x => isNaN(Number(x)));
    this.dom.$recurrenceWeeklyFormField.Input.options = Array.from(daysArray, element => ({
      label: this.translations.weekdays.shortNames[element],
      value: parseInt(WeekDays[element], 10),
    }));
  }

  private setReconfigureSchedulerDropdownOptions(): void {
    this.dom.$recurrenceFormField.Input.options = [
      {
        title: translateFn('By hour'),
        value: RECURRENCE_OPTIONS[RecurrenceType.ByHour].value,
      },
      {
        title: translateFn('Daily'),
        value: RECURRENCE_OPTIONS[RecurrenceType.Daily].value,
      },
      {
        title: translateFn('Weekly'),
        value: RECURRENCE_OPTIONS[RecurrenceType.Weekly].value,
      },
    ];
  }

  public setWeekDaysValue(weekDay: WeekDays): void {
    this.dom.$recurrenceWeeklyFormField.Input.setFormValue([weekDay]);
  }

  public toggleScheduledSectionVisibility(visible: boolean): void {
    toggleVisibility(this.dom.$scheduledSection, visible);
  }

  public toggleRecurrenceOptionVisibility(recurrenceOption: number): void {
    switch (recurrenceOption) {
      case RECURRENCE_OPTIONS[RecurrenceType.ByHour].value:
        toggleVisibility(this.dom.$recurrenceByHourFormField, true);
        toggleVisibility(this.dom.$recurrenceDailyStartTimeFormField, false);
        toggleVisibility(this.dom.$recurrenceWeeklyFormField, false);
        break;
      case RECURRENCE_OPTIONS[RecurrenceType.Daily].value:
        toggleVisibility(this.dom.$recurrenceByHourFormField, false);
        toggleVisibility(this.dom.$recurrenceDailyStartTimeFormField, true);
        toggleVisibility(this.dom.$recurrenceWeeklyFormField, false);
        break;
      case RECURRENCE_OPTIONS[RecurrenceType.Weekly].value:
        toggleVisibility(this.dom.$recurrenceByHourFormField, false);
        toggleVisibility(this.dom.$recurrenceDailyStartTimeFormField, true);
        toggleVisibility(this.dom.$recurrenceWeeklyFormField, true);
        break;
    }
    this.setStartTimeAndByHourDefaultValue();
  }
}
